import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
} from "react";
import { useSearchParams } from "react-router-dom";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { ArrowRight, Loader2 } from "lucide-react";

import checkAnimation from "@/shared/assets/animations/check.lottie?url";
import logoFull from "@/shared/assets/logo/logo-full.svg";
import papersIcon from "@/shared/assets/icons/papers-icon.svg";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { FileCard } from "@/shared/components/ui/file-card";
import {
  ACCEPTED_LABEL,
  ACCEPTED_TYPES,
  uploadZoneClasses,
} from "@/shared/components/ui/upload-zone";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";
import { convertImageFileToPdf } from "@/shared/utils/convertImageFileToPdf";

import type { FileStatus, UploadedFile } from "@/shared/types/uploadTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { getRequestError } from "./requestErrors";

interface SessionInfo {
  remaining_uploads: number;
  requested_document_type: string | null;
  requester_first_name: string | null;
  decedent_first_name: string | null;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export const DocumentRequestPage: FC = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/documents/request/${token}/validate`);

        if (!res.ok) {
          const body = await res.json();
          const reason = body.error || "Unknown validation error";
          setSessionError(reason);
          sentryUtils.captureMessage(
            `Document request validation failed: ${reason}`,
            "warning",
            { status: res.status },
          );
          setLoading(false);
          return;
        }

        const data = await res.json();
        setSessionInfo({
          remaining_uploads: data.remaining_uploads,
          requested_document_type: data.requested_document_type,
          requester_first_name: data.requester_first_name ?? null,
          decedent_first_name: data.decedent_first_name ?? null,
        });
        setRemaining(data.remaining_uploads);
        setLoading(false);
      } catch (err) {
        setIsNetworkError(true);
        setSessionError("network_error");
        sentryUtils.captureError(err, "Document request session load failed");
        setLoading(false);
      }
    })();
  }, [token]);

  const canUpload = useMemo(
    () => Boolean(token && sessionInfo && remaining > 0),
    [token, sessionInfo, remaining],
  );

  const addFiles = useCallback((files: File[]) => {
    if (files.length > 0) {
      setUploadedFiles((prev) => [
        ...prev,
        ...files.map((file) => ({ file, status: "idle" as FileStatus })),
      ]);
    }
  }, []);

  const handleSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      addFiles(Array.from(e.target.files || []));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(Array.from(e.dataTransfer.files));
    },
    [addFiles],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!canUpload || uploadedFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);

    let hasFailure = false;
    let currentRemaining = remaining;

    for (let i = 0; i < uploadedFiles.length; i++) {
      if (currentRemaining <= 0) break;

      const { file } = uploadedFiles[i];
      setUploadedFiles((prev) =>
        prev.map((uf, idx) => (idx === i ? { ...uf, status: "loading" as FileStatus } : uf)),
      );

      try {
        let toUpload = file;
        let finalName = file.name;
        const isImage =
          file.type.startsWith("image/") ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif");
        if (isImage) {
          const converted = await convertImageFileToPdf(file);
          toUpload = converted;
          finalName = converted.name;
        }

        const presignedRes = await fetch(
          `${SUPABASE_URL}/functions/v1/documents/request/${token}/presigned-url`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fileName: finalName }),
          },
        );

        if (!presignedRes.ok) {
          const errBody = await presignedRes.json();
          throw new Error(errBody.error || `Failed to get upload URL (${presignedRes.status})`);
        }

        const {
          uploadUrl,
          fileKey,
          remaining_uploads: serverRemaining,
        } = await presignedRes.json();
        currentRemaining = serverRemaining;
        setRemaining(serverRemaining);

        if (serverRemaining <= 0) {
          throw new Error("Upload limit reached");
        }

        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: toUpload,
          headers: { "Content-Type": toUpload.type || "application/octet-stream" },
        });

        if (!putRes.ok) {
          throw new Error(`Upload failed: ${putRes.status}`);
        }

        const metaRes = await fetch(
          `${SUPABASE_URL}/functions/v1/documents/request/${token}/metadata`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              file_key: fileKey,
              file_name: finalName,
              document_type: sessionInfo?.requested_document_type || "other",
              file_size: toUpload.size,
            }),
          },
        );

        if (!metaRes.ok) {
          const errBody = await metaRes.json();
          throw new Error(errBody.error || `Failed to save document (${metaRes.status})`);
        }

        setUploadedFiles((prev) =>
          prev.map((uf, idx) => (idx === i ? { ...uf, status: "complete" as FileStatus } : uf)),
        );
      } catch (e) {
        hasFailure = true;
        const msg = e instanceof Error ? e.message : "Upload failed";
        setUploadError(msg);
        sentryUtils.captureError(e, "Document request upload failed");
        setUploadedFiles((prev) =>
          prev.map((uf, idx) => (idx === i ? { ...uf, status: "error" as FileStatus } : uf)),
        );
      }
    }

    if (!hasFailure) {
      setSuccess(true);
      setUploadedFiles([]);
    }

    setUploading(false);
  }, [canUpload, uploadedFiles, token, remaining, sessionInfo]);

  const docTypeLabel = sessionInfo?.requested_document_type
    ? getDocumentTypeLabel(sessionInfo.requested_document_type)
    : "document";
  const requesterName = sessionInfo?.requester_first_name || "[Name]";
  const decedentName = sessionInfo?.decedent_first_name || "[Name]";
  const header = (
    <header className="shrink-0 w-full border-b border-primary">
      <div className="flex items-center justify-center bg-white px-4 py-4 sm:px-6 sm:py-6">
        <img src={logoFull} alt="Elayne" className="h-7" />
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-subtle">
        {header}
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand" />
        </div>
      </div>
    );
  }

  if (!token || sessionError) {
    const { heading, description } = getRequestError({
      token,
      isNetworkError,
      serverError: sessionError,
    });

    return (
      <div className="flex min-h-screen flex-col bg-subtle">
        {header}
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <h3 className="mb-2">{heading}</h3>
            <p className="text-secondary">{description}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen flex-col bg-subtle">
        {header}
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-3 h-16 w-16">
              <DotLottieReact src={checkAnimation} loop={false} autoplay />
            </div>
            <h3 className="mb-1">Documents uploaded</h3>
            <p className="text-pretty text-secondary">
              Your documents have been received. You can close this tab.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const title = `Upload ${decedentName}'s ${docTypeLabel.toLowerCase()}`;
  const description = `${requesterName} is settling ${decedentName}'s estate and asked for your help. Your upload goes directly to ${decedentName}'s estate file on Elayne.`;
  const securityNote = `Files are encrypted and only shared with people ${requesterName} has invited to ${decedentName}'s estate.`;

  return (
    <div className="flex min-h-screen flex-col bg-subtle">
      {header}
      <main className="flex flex-1 flex-col items-center px-4 pt-12 pb-16 sm:pt-16">
        <div className="w-full max-w-lg">
          <h2 className="text-center">{title}</h2>
          <p className="mt-3 text-center text-pretty text-secondary">{description}</p>

          <div className="mt-8">
            <p className="mb-2 text-sm font-medium">{docTypeLabel}</p>
            {uploadedFiles.length === 0 && (
              <label
                htmlFor="file-input"
                className={uploadZoneClasses(isDragging)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <img src={papersIcon} alt="Upload documents" className="mb-3 h-14 w-14" />
                <p className="font-medium">Click to upload or drag and drop</p>
                <p className="text-tertiary">{ACCEPTED_LABEL}</p>
              </label>
            )}
            <input
              ref={fileInputRef}
              id="file-input"
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              onChange={handleSelect}
              className="hidden"
            />
          </div>

          {uploadedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              {uploadedFiles.map((uploadedFile, idx) => (
                <FileCard
                  key={`${uploadedFile.file.name}-${idx}`}
                  fileName={uploadedFile.file.name}
                  fileSize={uploadedFile.file.size}
                  status={uploadedFile.status}
                  onRemove={() => handleRemoveFile(idx)}
                  isRemoving={uploading}
                />
              ))}
            </div>
          )}

          <SecurityNote className="mt-4" size="lg" align="left" text={securityNote} />

          <FieldError message={uploadError} className="mt-4" />

          <Button
            variant="brand"
            rounded={true}
            className="mt-8 w-full"
            size="lg"
            onClick={handleUpload}
            disabled={uploading || uploadedFiles.length === 0 || remaining <= 0}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Submit <ArrowRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};
